import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PanelLayout from '@/components/PanelLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import { trackEvent } from '@/utils/tracking';
import {
  Loader2, Sparkles, ArrowRight, ArrowDown, Lightbulb, Copy, CheckCircle2,
  Globe, FileText, Flame, TrendingUp, AlertTriangle, X,
  Star, Zap, Target, BookOpen, Send, Info, ChevronRight, MousePointerClick, Clock, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

/* ── Score badge ── */
const SCORE_COLORS = (s) => {
  if (s >= 8) return { icon: Flame, color: '#EF4444', bg: '#FEE2E2', label: 'Mocny post!' };
  if (s >= 5) return { icon: TrendingUp, color: '#F59E0B', bg: '#FEF3C7', label: 'Niezły' };
  return { icon: AlertTriangle, color: '#94A3B8', bg: '#F1F5F9', label: 'Słaby' };
};

/* ── Quick examples per profile type ── */
const EXAMPLES_HODOWLA = [
  { text: "Nasze szczeniaki juz szukaja domkow! Labradorki czekoladowe, 8 tyg, FCI. Pisz na priv!", label: "Ogloszenie miotu" },
  { text: "10 lat hodowli, setki szczęśliwych rodzin. Dziś dostaliśmy te zdjęcia od naszych szczeniaczków — duże, zdrowe, kochane!", label: "Historia hodowli" },
  { text: "Czy wiesz, ze labradory to najbardziej rodzinna rasa? Oto 5 powodow dlaczego warto miec labradora w domu...", label: "Post edukacyjny" },
  { text: "OSTATNIE 2 SZCZENIAKI! Gotowe do odbioru od zaraz. Zaszczepione, odrobaczone, z ksiazeczka zdrowia i rodowodem FCI.", label: "Pilna oferta" },
];

const EXAMPLES_BIZNES = [
  { text: "Nasza firma zrealizowala juz 500 zamowien w tym roku! Dziekujemy za zaufanie — kazdy klient to dla nas motywacja.", label: "Social proof" },
  { text: "5 powodow dlaczego warto wybrac profesjonalny serwis zamiast robic to samemu. Numer 3 Cie zaskoczy!", label: "Post edukacyjny" },
  { text: "PROMOCJA! Tylko do konca tygodnia -20% na wszystkie uslugi. Napisz do nas po kod rabatowy!", label: "Promocja" },
  { text: "Za kulisami naszej pracy — tak wyglada realizacja zamowienia krok po kroku. Od projektu do efektu!", label: "Kulisy firmy" },
];

/* ═══════════════════════════════════════════
   STEP INDICATOR — wizualny pasek postepu
   ═══════════════════════════════════════════ */
function StepBar({ current }) {
  const steps = [
    { n: 1, label: 'Wklej post' },
    { n: 2, label: 'Analiza' },
    { n: 3, label: 'Twój lepszy post' },
  ];
  return (
    <div className="flex items-center justify-center gap-0 mb-8" data-testid="step-bar">
      {steps.map((s, i) => {
        const done = current > s.n;
        const active = current === s.n;
        return (
          <div key={s.n} className="flex items-center">
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
              done ? 'bg-emerald-50' : active ? 'bg-[#F47A3B]/10 ring-2 ring-[#F47A3B]/20' : 'bg-zinc-50'
            }`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                done ? 'bg-emerald-500 text-white' : active ? 'bg-[#F47A3B] text-white' : 'bg-zinc-200 text-zinc-400'
              }`}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : s.n}
              </div>
              <span className={`text-sm font-semibold transition-colors ${
                done ? 'text-emerald-600' : active ? 'text-[#F47A3B]' : 'text-zinc-400'
              }`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <ChevronRight className={`w-5 h-5 mx-1 ${done ? 'text-emerald-300' : 'text-zinc-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export default function CompetitorWatch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [inputMode, setInputMode] = useState('post');
  const [inputText, setInputText] = useState('');
  const [inputUrl, setInputUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedPost, setGeneratedPost] = useState('');
  const [copied, setCopied] = useState(false);
  const [library, setLibrary] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [abHooks, setAbHooks] = useState(null);
  const [selectedHook, setSelectedHook] = useState(null);
  const [loadingHooks, setLoadingHooks] = useState(false);
  const [competitors, setCompetitors] = useState([]);
  const [showCompetitors, setShowCompetitors] = useState(false);
  const [newComp, setNewComp] = useState({ name: '', link: '', type: 'hodowla' });
  const [addingComp, setAddingComp] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const debounceRef = useRef(null);
  const autoGenerateRef = useRef(false);
  const resultRef = useRef(null);

  /* ── Profile type detection ── */
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isBiznes = user.profile_type === 'biznes';
  const QUICK_EXAMPLES = isBiznes ? EXAMPLES_BIZNES : EXAMPLES_HODOWLA;

  useEffect(() => {
    axios.get(`${API}/inspiration/library`, { headers })
      .then(r => setLibrary(r.data || []))
      .catch(() => {});
    axios.get(`${API}/competitors`, { headers })
      .then(r => setCompetitors(r.data || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  /* ── Determine current step ── */
  const currentStep = generatedPost ? 3 : (analysis || analyzing) ? 2 : 1;

  /* ── AUTO-ANALYZE: debounce 1.5s after paste/type ── */
  const scheduleAutoAnalyze = useCallback((text, url, mode) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const hasContent = mode === 'post' ? text.trim().length > 20 : url.trim().length > 8;
    if (!hasContent) return;
    debounceRef.current = setTimeout(() => {
      autoGenerateRef.current = true;
      doAnalyze(text, url, mode);
    }, 1500);
  }, []); // eslint-disable-line

  const handleTextChange = (val) => {
    setInputText(val);
    setAnalysis(null);
    setGeneratedPost('');
    scheduleAutoAnalyze(val, inputUrl, 'post');
  };

  const handleUrlChange = (val) => {
    setInputUrl(val);
    setAnalysis(null);
    setGeneratedPost('');
    scheduleAutoAnalyze(inputText, val, 'website');
  };

  /* ── ANALYZE ── */
  const doAnalyze = async (text, url, mode) => {
    if (analyzing) return;
    const t = text || inputText;
    const u = url || inputUrl;
    const m = mode || inputMode;
    if (!t.trim() && !u.trim()) return;
    setAnalyzing(true);
    setAnalysis(null);
    setGeneratedPost('');
    try {
      const res = await axios.post(`${API}/inspiration/analyze`, {
        text: t, url: u, type: m,
      }, { headers });
      setAnalysis(res.data);
      if (autoGenerateRef.current) {
        autoGenerateRef.current = false;
        autoGenerate(res.data, m);
      }
    } catch {
      toast.error('Coś poszło nie tak — spróbuj jeszcze raz');
    }
    setAnalyzing(false);
  };

  const analyze = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    autoGenerateRef.current = true;
    doAnalyze(inputText, inputUrl, inputMode);
  };

  /* ── GENERATE POST ── */
  const autoGenerate = async (analysisData, mode) => {
    if (!analysisData) return;
    await doGeneratePost('better', analysisData, mode);
  };

  const doGeneratePost = async (genMode = 'better', analysisOverride, modeOverride) => {
    const a = analysisOverride || analysis;
    const m = modeOverride || inputMode;
    if (!a) return;
    setGenerating(true);
    try {
      const body = m === 'website'
        ? {
            topic: a.post_ideas?.[0]?.topic || a.what_they_do || '',
            hook: a.post_ideas?.[0]?.hook || '',
            style: a.style || 'engaging',
            competitor_name: a.competitor_name || '',
            reactions: a.post_ideas?.[0]?.score * 10 || 50,
            why_works: a.post_ideas?.[0]?.why || a.strategy_tip || '',
            mode: genMode,
          }
        : {
            topic: a.topic || '',
            hook: a.hook || a.better_hook || '',
            style: a.style || 'engaging',
            competitor_name: '',
            reactions: a.score * 10 || 50,
            why_works: a.why_works || '',
            mode: genMode,
          };
      const res = await axios.post(`${API}/autonomous/generate-inspired-post`, body, { headers });
      setGeneratedPost(res.data?.content || '');
      trackEvent('post_generated', { source: 'competitor_watch', topic: analysis?.topic });
      // Auto-generate A/B hooks
      setLoadingHooks(true);
      axios.post(`${API}/hooks/generate-ab`, {
        topic: analysis?.topic || '',
        context: inputText?.slice(0, 200) || '',
      }, { headers }).then(r => {
        if (r.data?.hooks) setAbHooks(r.data.hooks);
      }).catch(() => {}).finally(() => setLoadingHooks(false));
      // Scroll to result
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    } catch {
      toast.error('Nie udało się wygenerować posta');
    }
    setGenerating(false);
  };

  const copyPost = () => {
    navigator.clipboard.writeText(generatedPost);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(isBiznes ? 'Skopiowano! Teraz wklej na Facebooku lub stronie' : 'Skopiowano! Teraz wklej w grupie na Facebooku');
  };

  const publishPost = () => {
    if (selectedHook) trackEvent('hook_published', { variant: selectedHook, topic: analysis?.topic });
    navigate('/panel/publikacja', { state: { inspiredPost: generatedPost, inspiredTopic: analysis?.topic || '' } });
  };

  const saveInspo = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/inspiration/save`, {
        analysis, generated_post: generatedPost,
        source_url: inputUrl, source_text: inputText,
        input_type: inputMode,
      }, { headers });
      setLibrary(prev => [res.data, ...prev]);
      toast.success('Zapisano! Wroc do tej inspiracji kiedy chcesz');
    } catch { toast.error('Blad zapisu'); }
    setSaving(false);
  };

  const loadFromLibrary = (item) => {
    setAnalysis(item.analysis);
    setGeneratedPost(item.generated_post || '');
    setInputText(item.source_text || '');
    setInputUrl(item.source_url || '');
    setInputMode(item.input_type || 'post');
    setShowLibrary(false);
  };

  const applyQuickExample = (example) => {
    setInputMode('post');
    setInputText(example.text);
    setAnalysis(null);
    setGeneratedPost('');
    autoGenerateRef.current = true;
    doAnalyze(example.text, '', 'post');
  };

  const startOver = () => {
    setInputText('');
    setInputUrl('');
    setAnalysis(null);
    setGeneratedPost('');
  };

  const hasInput = inputText.trim().length > 0 || inputUrl.trim().length > 0;

  return (
    <PanelLayout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="inspiration-page">

        {/* Header */}
        <div className="text-center mb-2">
          <span className="inline-block px-3 py-1 rounded-full bg-[#F47A3B]/10 text-[#F47A3B] text-xs font-semibold mb-3">Zarabiaj więcej</span>
          <h1 className="text-3xl font-bold text-zinc-900 mb-2">Co działa u innych</h1>
          <p className="text-zinc-500 text-sm max-w-md mx-auto">
            {isBiznes
              ? 'Zobacz co publikuje konkurencja — my zrobimy to lepiej za Ciebie'
              : 'Zobacz co publikują inni hodowcy — my zrobimy to lepiej za Ciebie'
            }
          </p>
        </div>

        {/* Library button */}
        {library.length > 0 && (
          <div className="flex justify-center mb-4">
            <Button variant="outline" size="sm" onClick={() => setShowLibrary(!showLibrary)} className="text-xs rounded-xl" data-testid="library-btn">
              <BookOpen className="w-3.5 h-3.5 mr-1" /> Twoje zapisane inspiracje ({library.length})
            </Button>
          </div>
        )}

        {/* Competitors section */}
        <div className="flex justify-center mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCompetitors(!showCompetitors)}
            className="text-xs rounded-xl"
            data-testid="competitors-toggle-btn"
          >
            <Users className="w-3.5 h-3.5 mr-1" />
            Twoja konkurencja ({competitors.length})
          </Button>
        </div>

        {showCompetitors && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white border border-zinc-200 rounded-2xl p-5 mb-6"
            data-testid="competitors-panel"
          >
            <h3 className="text-sm font-bold text-zinc-800 mb-3">Zapisani konkurenci</h3>

            {/* Add competitor form */}
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <input
                value={newComp.name}
                onChange={e => setNewComp(p => ({ ...p, name: e.target.value }))}
                placeholder="Nazwa (np. Hodowla Złotych)"
                className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:border-[#F47A3B]"
                data-testid="comp-name-input"
              />
              <input
                value={newComp.link}
                onChange={e => setNewComp(p => ({ ...p, link: e.target.value }))}
                placeholder="Link FB (opcjonalny)"
                className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:border-[#F47A3B]"
                data-testid="comp-link-input"
              />
              <select
                value={newComp.type}
                onChange={e => setNewComp(p => ({ ...p, type: e.target.value }))}
                className="px-3 py-2 rounded-xl border border-zinc-200 text-sm bg-white"
                data-testid="comp-type-select"
              >
                <option value="hodowla">Hodowla</option>
                <option value="firma">Firma</option>
              </select>
              <Button
                onClick={async () => {
                  if (!newComp.name.trim()) return;
                  setAddingComp(true);
                  try {
                    const res = await axios.post(`${API}/competitors`, newComp, { headers });
                    setCompetitors(prev => [res.data, ...prev]);
                    setNewComp({ name: '', link: '', type: isBiznes ? 'firma' : 'hodowla' });
                    toast.success('Dodano konkurenta');
                  } catch {
                    toast.error('Nie udało się dodać');
                  } finally {
                    setAddingComp(false);
                  }
                }}
                disabled={addingComp || !newComp.name.trim()}
                size="sm"
                className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl px-4 text-sm"
                data-testid="add-comp-btn"
              >
                {addingComp ? <Loader2 className="w-4 h-4 animate-spin" /> : '+ Dodaj'}
              </Button>
            </div>

            {/* Competitors list */}
            {competitors.length === 0 ? (
              <p className="text-xs text-zinc-400 text-center py-3">Dodaj pierwszego konkurenta, żeby śledzić co u niego działa</p>
            ) : (
              <div className="space-y-2">
                {competitors.map(c => (
                  <div key={c.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-zinc-50 hover:bg-zinc-100 transition-colors group" data-testid={`competitor-${c.id}`}>
                    <div className="w-8 h-8 rounded-full bg-[#F47A3B]/10 flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-[#F47A3B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-800 truncate">{c.name}</p>
                      {c.link && (
                        <a href={c.link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#F47A3B] hover:underline truncate block">
                          {c.link.replace(/https?:\/\/(www\.)?/, '').slice(0, 40)}
                        </a>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${c.type === 'firma' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
                      {c.type === 'firma' ? 'Firma' : 'Hodowla'}
                    </span>
                    <button
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/competitors/${c.id}`, { headers });
                          setCompetitors(prev => prev.filter(x => x.id !== c.id));
                        } catch {}
                      }}
                      className="opacity-0 group-hover:opacity-100 text-zinc-300 hover:text-red-400 transition-all"
                      data-testid={`delete-comp-${c.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Step bar */}
        <StepBar current={currentStep} />

        {/* ═══════════════════════════════════════
            KROK 1: Wklej post
            ═══════════════════════════════════════ */}
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Mode toggle */}
              <div className="flex gap-2 justify-center mb-5">
                <button
                  onClick={() => setInputMode('post')}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${inputMode === 'post' ? 'bg-[#F47A3B] text-white shadow-lg shadow-[#F47A3B]/25' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                  data-testid="mode-post"
                >
                  <FileText className="w-4 h-4 inline mr-1.5" />{isBiznes ? 'Post konkurenta' : 'Post z Facebooka'}
                </button>
                <button
                  onClick={() => setInputMode('website')}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${inputMode === 'website' ? 'bg-[#F47A3B] text-white shadow-lg shadow-[#F47A3B]/25' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                  data-testid="mode-website"
                >
                  <Globe className="w-4 h-4 inline mr-1.5" />{isBiznes ? 'Strona konkurenta' : 'Strona hodowcy'}
                </button>
              </div>

              {/* Input area with clear instructions */}
              <div className="bg-white border-2 border-dashed border-zinc-200 rounded-2xl p-6 mb-4 hover:border-[#F47A3B]/30 transition-colors" data-testid="input-section">
                {inputMode === 'post' ? (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <MousePointerClick className="w-4 h-4 text-[#F47A3B]" />
                      <p className="text-sm font-semibold text-zinc-700">
                        {isBiznes
                          ? 'Skopiuj tekst posta konkurencyjnej firmy i wklej tutaj'
                          : 'Skopiuj tekst posta innego hodowcy i wklej tutaj'
                        }
                      </p>
                    </div>
                    <textarea
                      value={inputText}
                      onChange={e => handleTextChange(e.target.value)}
                      placeholder={isBiznes
                        ? "Wejdz na Facebook → znajdz post konkurencyjnej firmy → zaznacz tekst → skopiuj (Ctrl+C) → wklej tutaj (Ctrl+V)"
                        : "Wejdz na Facebook → znajdz post innego hodowcy → zaznacz tekst → skopiuj (Ctrl+C) → wklej tutaj (Ctrl+V)"
                      }
                      rows={5}
                      className="w-full px-4 py-3 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-[#F47A3B] focus:ring-2 focus:ring-[#F47A3B]/10 resize-none transition-all"
                      data-testid="input-text"
                    />
                    {inputText.trim().length > 0 && inputText.trim().length <= 20 && (
                      <p className="text-xs text-amber-500 mt-1 flex items-center gap-1">
                        <Info className="w-3 h-3" /> Wklej wiecej tekstu — minimum 20 znakow
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <MousePointerClick className="w-4 h-4 text-[#F47A3B]" />
                      <p className="text-sm font-semibold text-zinc-700">
                        {isBiznes
                          ? 'Wklej link do strony konkurencyjnej firmy'
                          : 'Wklej link do strony innego hodowcy'
                        }
                      </p>
                    </div>
                    <input
                      value={inputUrl}
                      onChange={e => handleUrlChange(e.target.value)}
                      placeholder={isBiznes ? "https://strona-konkurenta.pl" : "https://hodowla-konkurenta.pl"}
                      className="w-full px-4 py-3 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-[#F47A3B] focus:ring-2 focus:ring-[#F47A3B]/10 transition-all"
                      data-testid="input-url"
                    />
                  </>
                )}

                <Button
                  onClick={analyze}
                  disabled={analyzing || (!inputText.trim() && !inputUrl.trim()) || (inputMode === 'post' && inputText.trim().length <= 20)}
                  className="mt-4 bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl w-full py-6 text-base font-bold shadow-lg shadow-[#F47A3B]/20"
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analizuje...</>
                  ) : (
                    <><Sparkles className="w-5 h-5 mr-2" /> Analizuj i zrob lepszy post</>
                  )}
                </Button>

                {!hasInput && (
                  <p className="text-center text-xs text-zinc-400 mt-3">
                    Automatycznie przeanalizujemy po wklejeniu
                  </p>
                )}
              </div>

              {/* Quick examples — widoczne tylko gdy puste pole */}
              {!hasInput && (
                <div className="bg-zinc-50 rounded-2xl p-5 mt-2" data-testid="quick-inspirations">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-[#F47A3B]" />
                    <p className="text-sm font-bold text-zinc-800">
                      {isBiznes ? 'Nie masz posta konkurenta? Kliknij przyklad:' : 'Nie masz posta? Kliknij przyklad:'}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {QUICK_EXAMPLES.map((ex, i) => (
                      <button
                        key={i}
                        onClick={() => applyQuickExample(ex)}
                        className="text-left border border-zinc-200 bg-white rounded-xl p-3 hover:border-[#F47A3B]/40 hover:bg-[#F47A3B]/5 transition-all group"
                        data-testid={`quick-example-${i}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-[#F47A3B]/10 text-[#F47A3B] text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                          <span className="text-xs font-semibold text-zinc-700 group-hover:text-[#F47A3B]">{ex.label}</span>
                        </div>
                        <p className="text-[11px] text-zinc-400 mt-1 line-clamp-1 ml-7">{ex.text}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ═══════════════════════════════════════
              KROK 2: Analiza (auto → przechodzi do 3)
              ═══════════════════════════════════════ */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {analyzing && (
                <div className="bg-white rounded-2xl border border-zinc-100 p-12 text-center" data-testid="analyzing-state">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                    className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F47A3B]/10 flex items-center justify-center"
                  >
                    <Sparkles className="w-8 h-8 text-[#F47A3B]" />
                  </motion.div>
                  <h2 className="text-xl font-bold text-zinc-900 mb-2">
                    {isBiznes ? 'Analizuje post konkurenta...' : 'Analizuje post hodowcy...'}
                  </h2>
                  <p className="text-sm text-zinc-500">Za chwile zobaczysz dlaczego ten post dziala i dostaniesz lepszy</p>
                </div>
              )}

              {analysis && !generatedPost && !generating && (
                <div className="space-y-4">
                  {/* Analysis summary */}
                  <div className="bg-white rounded-2xl border border-zinc-100 p-6" data-testid="analysis-card">
                    {(() => {
                      const badge = SCORE_COLORS(analysis.score || 5);
                      return (
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-14 h-14 rounded-2xl flex flex-col items-center justify-center" style={{ background: badge.bg }}>
                            <badge.icon className="w-5 h-5" style={{ color: badge.color }} />
                            <span className="text-[10px] font-bold mt-0.5" style={{ color: badge.color }}>{analysis.score}/10</span>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-zinc-900">{analysis.topic || 'Analiza posta'}</p>
                            <p className="text-xs text-zinc-400">Styl: {analysis.style}</p>
                          </div>
                        </div>
                      );
                    })()}
                    {analysis.why_works && (
                      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 mb-4">
                        <p className="text-xs text-emerald-600 font-semibold mb-1 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Dlaczego ten post dziala</p>
                        <p className="text-sm text-emerald-800">{analysis.why_works}</p>
                      </div>
                    )}
                  </div>

                  {/* CTA to generate */}
                  <div className="flex gap-3">
                    <Button onClick={() => doGeneratePost('better')} className="flex-1 bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl py-6 text-base font-bold shadow-lg shadow-[#F47A3B]/20" data-testid="btn-better">
                      <Zap className="w-5 h-5 mr-2" /> Zrob mi lepszy post
                    </Button>
                    <Button onClick={() => doGeneratePost('similar')} variant="outline" className="rounded-xl py-6" data-testid="btn-similar">
                      <Target className="w-4 h-4 mr-1" /> Podobny
                    </Button>
                  </div>
                </div>
              )}

              {generating && (
                <div className="bg-white rounded-2xl border border-zinc-100 p-12 text-center" data-testid="generating-state">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center"
                  >
                    <Zap className="w-8 h-8 text-emerald-500" />
                  </motion.div>
                  <h2 className="text-xl font-bold text-zinc-900 mb-2">
                    {isBiznes ? 'Pisze lepszy post dla Twojej firmy...' : 'Pisze lepszy post dla Twojej hodowli...'}
                  </h2>
                  <p className="text-sm text-zinc-500">Jeszcze kilka sekund</p>
                </div>
              )}
            </motion.div>
          )}

          {/* ═══════════════════════════════════════
              KROK 3: QUICK COMPARE — Twój post vs oryginał
              ═══════════════════════════════════════ */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              ref={resultRef}
            >
              {/* Compare header */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", damping: 20 }}
                className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-5 text-center"
                data-testid="compare-header"
              >
                <p className="text-[15px] font-bold text-zinc-900">
                  Twój post przyciąga więcej uwagi i ma większą szansę na zapytania
                </p>
              </motion.div>

              {/* Side by side comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5" data-testid="compare-panels">
                {/* LEFT — Original */}
                <div className="bg-white border border-zinc-200 rounded-2xl p-4">
                  <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-3">Post konkurenta</p>
                  <p className="text-[13px] text-zinc-500 leading-relaxed whitespace-pre-wrap line-clamp-6">
                    {inputText || analysis?.source_text || 'Oryginał'}
                  </p>
                  {analysis?.score && (
                    <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-3">
                      <div className="flex-1">
                        <p className="text-[10px] text-zinc-400 mb-1">Uwaga</p>
                        <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full bg-zinc-300 rounded-full" style={{ width: `${Math.min(analysis.score * 10, 100)}%` }} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] text-zinc-400 mb-1">Zapytania</p>
                        <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full bg-zinc-300 rounded-full" style={{ width: `${Math.max((analysis.score - 2) * 12, 10)}%` }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* RIGHT — Generated (better) */}
                <div className="bg-white border-2 border-emerald-300 rounded-2xl p-4 relative">
                  <div className="absolute -top-2.5 left-4 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">TWÓJ POST</div>
                  <p className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider mb-3 mt-1">Wygenerowany post</p>
                  <p className="text-[13px] text-zinc-800 leading-relaxed whitespace-pre-wrap line-clamp-6 font-medium">
                    {generatedPost?.slice(0, 300)}{generatedPost?.length > 300 ? '...' : ''}
                  </p>
                  <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-[10px] text-emerald-500 mb-1">Uwaga</p>
                      <div className="h-1.5 bg-emerald-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-400 rounded-full" style={{ width: '90%' }} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] text-emerald-500 mb-1">Zapytania</p>
                      <div className="h-1.5 bg-emerald-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-400 rounded-full" style={{ width: '85%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advantages */}
              <div className="bg-white border border-zinc-100 rounded-2xl p-5 mb-5" data-testid="compare-advantages">
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 shrink-0" />
                    <span className="text-[13px] text-zinc-700 font-medium">Mocniejszy początek — zatrzymuje scroll</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 shrink-0" />
                    <span className="text-[13px] text-zinc-700 font-medium">Budzi emocje i zaufanie</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 shrink-0" />
                    <span className="text-[13px] text-zinc-700 font-medium">Prowadzi do kontaktu — ma jasne CTA</span>
                  </div>
                </div>

                {/* Timer hint */}
                <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-[#F47A3B]" />
                  <span className="text-[12px] text-[#F47A3B] font-semibold">
                    Najlepszy moment na publikację: teraz
                  </span>
                  <span className="text-[11px] text-zinc-400 ml-1">
                    ({new Date().toLocaleDateString('pl-PL', { weekday: 'long' })} 18:00–20:00 = najwięcej reakcji)
                  </span>
                </div>
              </div>

              {/* A/B Hook Selection */}
              {abHooks && (
                <div className="bg-white border border-zinc-200 rounded-2xl p-5 mb-5" data-testid="ab-hooks-panel">
                  <p className="text-[13px] font-bold text-zinc-800 mb-3">Który początek lepiej zadziała?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { key: 'problem', label: 'Problem', icon: '🔥', desc: abHooks.problem },
                      { key: 'wynik', label: 'Wynik', icon: '🏆', desc: abHooks.wynik },
                    ].map(h => (
                      <button
                        key={h.key}
                        onClick={() => {
                          setSelectedHook(h.key);
                          trackEvent('hook_selected', { variant: h.key, topic: analysis?.topic });
                          // Replace hook in generated post
                          if (generatedPost) {
                            const lines = generatedPost.split('\n');
                            lines[0] = h.desc;
                            setGeneratedPost(lines.join('\n'));
                          }
                        }}
                        className={`text-left p-4 rounded-xl border-2 transition-all ${
                          selectedHook === h.key
                            ? 'border-[#F47A3B] bg-[#F47A3B]/5'
                            : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                        data-testid={`hook-${h.key}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-sm">{h.icon}</span>
                          <span className={`text-[12px] font-semibold ${selectedHook === h.key ? 'text-[#F47A3B]' : 'text-zinc-500'}`}>{h.label}</span>
                        </div>
                        <p className="text-[13px] text-zinc-700 font-medium leading-snug">"{h.desc}"</p>
                      </button>
                    ))}
                  </div>
                  {loadingHooks && <p className="text-[11px] text-zinc-400 mt-2 text-center">Generuję hooki...</p>}
                </div>
              )}

              {/* CTA — Opublikuj teraz (PRIMARY) */}
              <div className="space-y-3" data-testid="compare-actions">
                <Button
                  onClick={publishPost}
                  className="w-full py-6 text-base font-bold rounded-xl bg-[#F47A3B] hover:bg-[#E06A2B] text-white shadow-lg shadow-[#F47A3B]/25 hover:shadow-xl transition-all"
                  data-testid="publish-now-btn"
                >
                  <Send className="w-5 h-5 mr-2" /> Opublikuj teraz
                </Button>

                <div className="flex gap-2">
                  <Button
                    onClick={copyPost}
                    variant="outline"
                    className={`flex-1 rounded-xl py-3 text-sm font-semibold ${copied ? 'border-emerald-300 text-emerald-600 bg-emerald-50' : 'border-zinc-200 text-zinc-600'}`}
                    data-testid="copy-post-btn"
                  >
                    {copied ? <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Skopiowano!</> : <><Copy className="w-4 h-4 mr-1.5" /> Kopiuj post</>}
                  </Button>
                  <Button
                    onClick={saveInspo}
                    disabled={saving}
                    variant="outline"
                    className="rounded-xl py-3 text-sm border-zinc-200"
                    data-testid="save-inspo-btn"
                  >
                    <Star className="w-4 h-4 mr-1" /> Zapisz
                  </Button>
                </div>

                {/* Secondary actions */}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => doGeneratePost('better')}
                    disabled={generating}
                    className="flex-1 text-center py-2 text-xs text-zinc-400 hover:text-[#F47A3B] transition-colors"
                    data-testid="regenerate-btn"
                  >
                    {generating ? <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> : <Zap className="w-3 h-3 inline mr-1" />}
                    Wygeneruj inny
                  </button>
                  <button
                    onClick={startOver}
                    className="flex-1 text-center py-2 text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
                    data-testid="start-over-btn"
                  >
                    <ArrowRight className="w-3 h-3 inline mr-1" /> Analizuj kolejny post
                  </button>
                </div>
              </div>

              {/* Analysis details — collapsed */}
              {analysis && (
                <details className="bg-white border border-zinc-100 rounded-2xl p-4 mt-5">
                  <summary className="text-sm font-semibold text-zinc-500 cursor-pointer hover:text-zinc-700">
                    Szczegóły analizy: {analysis.topic} ({analysis.score}/10)
                  </summary>
                  <div className="mt-3 space-y-3 text-sm text-zinc-600">
                    {analysis.hook && (
                      <p><span className="font-semibold text-zinc-700">Hook:</span> "{analysis.hook}"</p>
                    )}
                    {analysis.why_works && (
                      <p><span className="font-semibold text-zinc-700">Dlaczego działa:</span> {analysis.why_works}</p>
                    )}
                    {analysis.improvement_tips?.length > 0 && (
                      <div>
                        <p className="font-semibold text-zinc-700 mb-1">Co można poprawić:</p>
                        {analysis.improvement_tips.map((tip, i) => (
                          <p key={i} className="flex items-start gap-1.5 text-xs text-zinc-500 mb-0.5">
                            <ArrowRight className="w-3 h-3 text-[#F47A3B] mt-0.5 shrink-0" /> {tip}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ═══════════════════════════════════════
            LIBRARY PANEL
            ═══════════════════════════════════════ */}
        <AnimatePresence>
          {showLibrary && library.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 bg-white border border-zinc-100 rounded-2xl p-5 overflow-hidden"
              data-testid="library-panel"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-zinc-900">Twoje zapisane inspiracje</h3>
                <button onClick={() => setShowLibrary(false)} className="text-zinc-400 hover:text-zinc-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {library.map((item, i) => (
                  <div key={item.id || i} className="border border-zinc-100 rounded-xl p-3 cursor-pointer hover:bg-zinc-50 transition-colors flex items-center gap-3" onClick={() => loadFromLibrary(item)}>
                    <div className="w-8 h-8 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center shrink-0">
                      <Sparkles className="w-4 h-4 text-[#F47A3B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900 truncate">{item.analysis?.topic || item.analysis?.competitor_name || 'Inspiracja'}</p>
                      <p className="text-xs text-zinc-400 truncate mt-0.5">{item.generated_post ? item.generated_post.slice(0, 60) + '...' : ''}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 shrink-0" />
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PanelLayout>
  );
}
